import React, { useEffect, useState, useRef, useCallback } from 'react';
import Globe from 'react-globe.gl';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { X, Globe as GlobeIcon, Mail, Phone, Calendar, MapPin, Briefcase, Clock, Video, Shield, UserCheck } from 'lucide-react';
import { employeeDirectoryService } from '../../services/employee-directory';

interface CountryData {
  country: string;
  count: number;
  coordinates: [number, number];
  flag: string;
}

interface EmployeeGlobeProps {
  isOpen: boolean;
  onClose: () => void;
  countryCounts: Record<string, number>;
}

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  personalEmail?: string;
  companyEmail?: string;
  department: string;
  photoUrl?: string;
  country: string;
}

// Country coordinates mapping (moved outside component to avoid re-creation)
const countryCoordinates: Record<string, [number, number]> = {
    'United States of America': [39.8283, -98.5795],
    'Philippines': [12.8797, 121.7740],
    'Ghana': [7.9465, -1.0232],
    'Argentina': [-38.4161, -63.6167],
    'Mexico': [23.6345, -102.5528],
    'Czech Republic': [49.7437, 15.3386],
    'India': [20.5937, 78.9629],
    'Spain': [40.4637, -3.7492],
    'Indonesia': [-0.7893, 113.9213],
    'Pakistan': [30.3753, 69.3451],
    'France': [46.2276, 2.2137],
    'Nicaragua': [12.2650, -85.2072],
    'Romania': [45.9432, 24.9668],
    'Cambodia': [12.5657, 104.9910],
    'Serbia': [44.0165, 21.0059],
    'Germany': [51.1657, 10.4515],
    'Ecuador': [-1.8312, -78.1834],
    'Brazil': [-14.2350, -51.9253],
    'Hungary': [47.1625, 19.5033],
    'Bangladesh': [23.6850, 90.3563],
    'Egypt': [26.0975, 30.0444],
    'United Kingdom': [55.3781, -3.4360]
};

// Country flag mapping (moved outside component to avoid re-creation)
const getCountryFlag = (country: string): string => {
    const flagMap: Record<string, string> = {
      'United States of America': '🇺🇸',
      'Philippines': '🇵🇭',
      'Ghana': '🇬🇭',
      'Argentina': '🇦🇷',
      'Mexico': '🇲🇽',
      'Czech Republic': '🇨🇿',
      'India': '🇮🇳',
      'Spain': '🇪🇸',
      'Indonesia': '🇮🇩',
      'Pakistan': '🇵🇰',
      'France': '🇫🇷',
      'Nicaragua': '🇳🇮',
      'Romania': '🇷🇴',
      'Cambodia': '🇰🇭',
      'Serbia': '🇷🇸',
      'Germany': '🇩🇪',
      'Ecuador': '🇪🇨',
      'Brazil': '🇧🇷',
      'Hungary': '🇭🇺',
      'Bangladesh': '🇧🇩',
      'Egypt': '🇪🇬',
      'United Kingdom': '🇬🇧'
    };
    return flagMap[country] || '🌍';
};

// Get color based on employee count
const getPointColor = (count: number): string => {
    if (count >= 20) return '#ef4444'; // red for 20+
    if (count >= 10) return '#f97316'; // orange for 10-19
    if (count >= 5) return '#eab308';  // yellow for 5-9
    if (count >= 2) return '#22c55e';  // green for 2-4
    return '#3b82f6'; // blue for 1
};

// Get point size based on employee count
const getPointSize = (count: number): number => {
    return Math.max(0.1, Math.min(1.0, count / 20));
};

const EmployeeGlobe: React.FC<EmployeeGlobeProps> = ({ isOpen, onClose, countryCounts }) => {
  const [globeData, setGlobeData] = useState<CountryData[]>([]);
  const globeEl = useRef<any>(null);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [countryEmployees, setCountryEmployees] = useState<Employee[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);

  useEffect(() => {
    if (countryCounts && Object.keys(countryCounts).length > 0) {
      const data: CountryData[] = Object.entries(countryCounts)
        .map(([country, count]) => {
          const coordinates = countryCoordinates[country];
          if (!coordinates) {
            console.warn(`No coordinates found for country: ${country}`);
            return null;
          }

          return {
            country,
            count,
            coordinates,
            flag: getCountryFlag(country),
            lat: coordinates[0],
            lng: coordinates[1],
            size: getPointSize(count),
            color: getPointColor(count)
          };
        })
        .filter(Boolean) as CountryData[];

      setGlobeData(data);
    }
  }, [countryCounts]);

  // Handle window resize and globe controls
  useEffect(() => {
    if (globeEl.current && isOpen) {
      // Disable auto-rotation - globe will only rotate when dragged
      globeEl.current.controls().autoRotate = false;
      
      // Handle window resize for fullscreen
      const handleResize = () => {
        if (globeEl.current) {
          globeEl.current.width(window.innerWidth);
          globeEl.current.height(window.innerHeight);
        }
      };
      
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isOpen]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (showEmployeeModal) {
          setShowEmployeeModal(false);
        } else if (isOpen) {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      return () => {
        document.removeEventListener('keydown', handleEscKey);
      };
    }
  }, [isOpen, showEmployeeModal, onClose]);

  // Load employees for a specific country
  const loadCountryEmployees = async (country: string) => {
    try {
      setEmployeesLoading(true);
      const employees = await employeeDirectoryService.getAllEmployees();
      const employeesFromCountry = employees.filter((employee: any) =>
        employee.country === country
      );
      setCountryEmployees(employeesFromCountry);
    } catch (error) {
      console.error('Error loading country employees:', error);
    } finally {
      setEmployeesLoading(false);
    }
  };

  // Handle pin click
  const handlePinClick = useCallback(async (country: string) => {
    setSelectedCountry(country);
    setShowEmployeeModal(true);
    await loadCountryEmployees(country);
  }, []);


  // Handle email click (same as in Dashboard)
  const handleEmailClick = (email: string, e: React.MouseEvent) => {
    e.preventDefault();
    
    try {
      const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(email)}`;
      window.open(gmailUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Failed to open Gmail:', error);
      
      try {
        window.open(`mailto:${email}`, '_blank', 'noopener,noreferrer,width=600,height=400');
      } catch (mailtoError) {
        console.error('Failed to open mailto:', mailtoError);
        
        try {
          navigator.clipboard.writeText(email).then(() => {
            const userChoice = window.confirm(
              `Email address copied to clipboard: ${email}\n\n` +
              `Click OK to open Gmail in a new tab, or Cancel to use your preferred email client.`
            );
            
            if (userChoice) {
              window.open('https://mail.google.com/mail/?view=cm&fs=1', '_blank');
            }
          }).catch(() => {
            const textArea = document.createElement('textarea');
            textArea.value = email;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            
            const userChoice = window.confirm(
              `Email address copied to clipboard: ${email}\n\n` +
              `Click OK to open Gmail in a new tab, or Cancel to use your preferred email client.`
            );
            
            if (userChoice) {
              window.open('https://mail.google.com/mail/?view=cm&fs=1', '_blank');
            }
          });
        } catch (clipboardError) {
          console.error('All email methods failed:', clipboardError);
          alert(`Please copy this email address manually: ${email}`);
        }
      }
    }
  };

  if (!isOpen) return null;

  const totalEmployees = Object.values(countryCounts).reduce((a, b) => a + b, 0);

  return (
    <div className="fixed inset-0 bg-black z-50" style={{ margin: 0, padding: 0, top: 0, left: 0, right: 0, bottom: 0 }}>
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 flex justify-between items-center p-6 bg-black/50 backdrop-blur" style={{ margin: 0 }}>
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2 text-white">
            <GlobeIcon className="h-6 w-6" />
            Global Employee Distribution
          </h2>
          <p className="text-sm text-gray-300 mt-1">
            {totalEmployees} employees across {Object.keys(countryCounts).length} countries
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-white/20">
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Globe Container - Full Screen */}
      <div className="relative w-full h-full bg-black">
        <Globe
          ref={globeEl}
          globeImageUrl="//unpkg.com/three-globe/example/img/earth-night.jpg"
          bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
          backgroundImageUrl="//unpkg.com/three-globe/example/img/night-sky.png"
          
          // Back to Points approach - flat pins with better alignment
          pointsData={globeData}
          pointLat="lat"
          pointLng="lng"
          pointColor="color"
          pointAltitude={0}
          pointRadius={1.5}
          pointResolution={8}
          
          // Click handler for points
          onPointClick={(point: any) => {
            console.log('Point clicked:', point);
            if (point && point.country) {
              handlePinClick(point.country);
            }
          }}
          
          // Point labels - clean hover tooltip only
          pointLabel={(d: any) => `
            <div style="
              background: rgba(0, 0, 0, 0.8);
              color: white;
              padding: 8px 12px;
              border-radius: 6px;
              font-size: 14px;
              font-family: system-ui, -apple-system, sans-serif;
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
              border: 1px solid rgba(255, 255, 255, 0.1);
            ">
              <div style="font-weight: bold; margin-bottom: 4px;">
                ${d.flag} ${d.country}
              </div>
              <div style="color: #e5e7eb;">
                ${d.count} employee${d.count !== 1 ? 's' : ''} - Click to view details
              </div>
            </div>
          `}
          
          // Try to add stable numbers directly on pins using labelsData
          labelsData={globeData}
          labelLat="lat"
          labelLng="lng"
          labelText={(d: any) => d.count.toString()}
          labelSize={1.0}
          labelDotRadius={0}
          labelColor={() => 'white'}
          labelResolution={6}
          labelAltitude={0.01}
          labelIncludeDot={false}
          labelsTransitionDuration={0}
          
          // Animation settings
          pointsTransitionDuration={2000}
          
          // Controls
          enablePointerInteraction={true}
          
          // Atmosphere
          atmosphereColor="#4f46e5"
          atmosphereAltitude={0.15}
          
          // Globe size and positioning
          width={window.innerWidth}
          height={window.innerHeight}
        />
        
        {/* Legend */}
        <div className="absolute bottom-6 left-6 bg-black/70 backdrop-blur rounded-lg p-4 border border-white/20">
          <h3 className="font-semibold mb-3 text-sm text-white">Employee Count Legend</h3>
          <div className="space-y-2 text-xs text-gray-300">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span>1 employee</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span>2-4 employees</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <span>5-9 employees</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-orange-500"></div>
              <span>10-19 employees</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span>20+ employees</span>
            </div>
          </div>
        </div>

        {/* Controls Info */}
        <div className="absolute bottom-6 right-6 bg-black/70 backdrop-blur rounded-lg p-4 border border-white/20">
          <h3 className="font-semibold mb-2 text-sm text-white">Controls</h3>
          <div className="space-y-1 text-xs text-gray-300">
            <div>🖱️ Drag to rotate</div>
            <div>🔍 Scroll to zoom</div>
            <div>👆 Hover for details</div>
            <div>⌨️ Press ESC to close</div>
          </div>
        </div>
      </div>

      {/* Country Employees Modal */}
      {showEmployeeModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
          onClick={() => setShowEmployeeModal(false)}
        >
          <div
            className="bg-card rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-background border-b p-6 flex justify-between items-center z-10">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <GlobeIcon className="h-6 w-6" />
                Employees from {selectedCountry}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowEmployeeModal(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="p-6">
              {employeesLoading ? (
                <div className="space-y-4">
                  {[1,2,3,4,5].map(i => (
                    <div key={i} className="flex items-center gap-4 p-4 border rounded-lg animate-pulse">
                      <div className="w-12 h-12 bg-muted rounded-full"></div>
                      <div className="flex-1">
                        <div className="w-32 h-4 bg-muted rounded mb-2"></div>
                        <div className="w-48 h-3 bg-muted rounded mb-1"></div>
                        <div className="w-24 h-3 bg-muted rounded"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : countryEmployees.length > 0 ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {countryEmployees.map((employee) => (
                      <Card
                        key={employee.id}
                        className="cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-lg bg-gray-100 dark:bg-gray-800/50"
                        onClick={() => setSelectedEmployee(employee)}
                      >
                        <CardContent className="p-4">
                          <div className="flex flex-col items-center space-y-3">
                            <Avatar className="w-16 h-16">
                              <AvatarImage
                                src={(employee as any).photoUrl || ''}
                                alt={`${employee.firstName} ${employee.lastName}`}
                                className="object-contain"
                              />
                              <AvatarFallback className="bg-primary/10 text-primary font-semibold text-lg">
                                {employee.firstName[0]}{employee.lastName[0]}
                              </AvatarFallback>
                            </Avatar>
                            <div className="text-center">
                              <h3 className="font-semibold text-base">
                                {employee.firstName} {employee.lastName}
                              </h3>
                              <Badge variant="secondary" className="mt-2">
                                {employee.department}
                              </Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  
                  <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                    <h3 className="font-semibold mb-2">Summary</h3>
                    <p className="text-sm text-muted-foreground">
                      {countryEmployees.length} employee{countryEmployees.length !== 1 ? 's' : ''} from {selectedCountry}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <GlobeIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Employees Found</h3>
                  <p className="text-muted-foreground">No employees found from {selectedCountry}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Employee Detail Modal - Matching Dashboard Format Exactly */}
      {selectedEmployee && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 99999,
            margin: 0,
            pointerEvents: 'all'
          }}
          onClick={() => setSelectedEmployee(null)}
        >
          <div
            className="bg-background rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto relative"
            style={{ pointerEvents: 'all' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-background border-b p-6 flex justify-between items-center z-10">
              <h2 className="text-2xl font-bold">
                {selectedEmployee.firstName} {selectedEmployee.lastName}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedEmployee(null)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="p-6 pt-8">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Profile Section */}
                <div className="lg:col-span-1">
                  <Card className="bg-gray-100 dark:bg-gray-800/50">
                    <CardContent className="p-6 text-center">
                      <div className="mb-6 mt-4">
                        <Avatar className="w-40 h-40 mx-auto">
                          <AvatarImage
                            src={(selectedEmployee as any).profileImageUrl || (selectedEmployee as any).photoUrl || ''}
                            alt={`${selectedEmployee.firstName} ${selectedEmployee.lastName}`}
                            className="object-contain"
                          />
                          <AvatarFallback className="bg-primary/10 text-primary font-semibold text-4xl">
                            {selectedEmployee.firstName?.charAt(0)}{selectedEmployee.lastName?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                      <h3 className="text-xl font-bold mb-2">
                        {selectedEmployee.firstName} {selectedEmployee.lastName}
                      </h3>
                      {(selectedEmployee as any).preferredName && (
                        <p className="text-muted-foreground mb-2">
                          Preferred: {(selectedEmployee as any).preferredName}
                        </p>
                      )}
                      <Badge variant="default" className="mb-4">
                        {selectedEmployee.department}
                      </Badge>
                      {(selectedEmployee as any).namePronunciation && (
                        <p className="text-sm text-muted-foreground">
                          Pronunciation: {(selectedEmployee as any).namePronunciation}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Details Section */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Contact Information */}
                  <Card className="bg-gray-100 dark:bg-gray-800/50">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Mail className="h-5 w-5" />
                        Contact Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {((selectedEmployee as any).companyEmail || (selectedEmployee as any).email) && (
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Company:</span>
                          <button
                            onClick={(e) => handleEmailClick((selectedEmployee as any).companyEmail || (selectedEmployee as any).email || '', e)}
                            className="text-[#2563EB] dark:text-[#3B82F6] hover:underline text-left"
                          >
                            {(selectedEmployee as any).companyEmail || (selectedEmployee as any).email}
                          </button>
                        </div>
                      )}
                      {(selectedEmployee as any).personalEmail && (
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Personal:</span>
                          <button
                            onClick={(e) => handleEmailClick((selectedEmployee as any).personalEmail || '', e)}
                            className="text-[#2563EB] dark:text-[#3B82F6] hover:underline text-left"
                          >
                            {(selectedEmployee as any).personalEmail}
                          </button>
                        </div>
                      )}
                      {(selectedEmployee as any).phoneNumber && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Phone:</span>
                          <span>{(selectedEmployee as any).phoneNumber}</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Work Information */}
                  <Card className="bg-gray-100 dark:bg-gray-800/50">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Briefcase className="h-5 w-5" />
                        Work Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {(selectedEmployee as any).startDate && (
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Start Date:</span>
                          <span>{new Date((selectedEmployee as any).startDate).toLocaleDateString()}</span>
                        </div>
                      )}
                      {(selectedEmployee as any).timezone && (
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Timezone:</span>
                          <span>{(selectedEmployee as any).timezone}</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Location Information */}
                  <Card className="bg-gray-100 dark:bg-gray-800/50">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <MapPin className="h-5 w-5" />
                        Location
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {selectedEmployee.country && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Country:</span>
                          <span>{selectedEmployee.country}</span>
                        </div>
                      )}
                      {(selectedEmployee as any).city && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">City:</span>
                          <span>{(selectedEmployee as any).city}</span>
                        </div>
                      )}
                      {(selectedEmployee as any).state && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">State:</span>
                          <span>{(selectedEmployee as any).state}</span>
                        </div>
                      )}
                      {(selectedEmployee as any).address1 && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Address:</span>
                          <span>{(selectedEmployee as any).address1}</span>
                        </div>
                      )}
                      {(selectedEmployee as any).zipCode && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Zip Code:</span>
                          <span>{(selectedEmployee as any).zipCode}</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Personal Information */}
                  {(selectedEmployee as any).birthday && (
                    <Card className="bg-gray-100 dark:bg-gray-800/50">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Calendar className="h-5 w-5" />
                          Personal Information
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Birthday:</span>
                          <span>{new Date((selectedEmployee as any).birthday).toLocaleDateString()}</span>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Emergency Contacts */}
                  {((selectedEmployee as any).emergencyContact1Name || (selectedEmployee as any).emergencyContact2Name) && (
                    <Card className="bg-gray-100 dark:bg-gray-800/50">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Shield className="h-5 w-5" />
                          Emergency Contacts
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {(selectedEmployee as any).emergencyContact1Name && (
                          <div>
                            <h4 className="font-medium flex items-center gap-2">
                              <UserCheck className="h-4 w-4 text-muted-foreground" />
                              Primary Contact
                            </h4>
                            <p>{(selectedEmployee as any).emergencyContact1Name}</p>
                            {(selectedEmployee as any).emergencyContact1Method && (
                              <p className="text-sm text-muted-foreground">{(selectedEmployee as any).emergencyContact1Method}</p>
                            )}
                            {(selectedEmployee as any).emergencyContact1Relationship && (
                              <p className="text-sm text-muted-foreground">Relationship: {(selectedEmployee as any).emergencyContact1Relationship}</p>
                            )}
                          </div>
                        )}
                        {(selectedEmployee as any).emergencyContact2Name && (
                          <div>
                            <h4 className="font-medium flex items-center gap-2">
                              <UserCheck className="h-4 w-4 text-muted-foreground" />
                              Secondary Contact
                            </h4>
                            <p>{(selectedEmployee as any).emergencyContact2Name}</p>
                            {(selectedEmployee as any).emergencyContact2Method && (
                              <p className="text-sm text-muted-foreground">{(selectedEmployee as any).emergencyContact2Method}</p>
                            )}
                            {(selectedEmployee as any).emergencyContact2Relationship && (
                              <p className="text-sm text-muted-foreground">Relationship: {(selectedEmployee as any).emergencyContact2Relationship}</p>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Additional Information */}
                  {((selectedEmployee as any).workVideo || (selectedEmployee as any).contactAuthorities || (selectedEmployee as any).lastUpdate) && (
                    <Card className="bg-gray-100 dark:bg-gray-800/50">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Briefcase className="h-5 w-5" />
                          Additional Information
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {(selectedEmployee as any).workVideo && (
                          <div className="flex items-center gap-2">
                            <Video className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">Work Video:</span>
                            <a
                              href={(selectedEmployee as any).workVideo}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#2563EB] dark:text-[#3B82F6] hover:underline"
                            >
                              View Video
                            </a>
                          </div>
                        )}
                        {(selectedEmployee as any).contactAuthorities && (
                          <div className="flex items-center gap-2">
                            <Shield className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">Contact Authorities:</span>
                            <span>{(selectedEmployee as any).contactAuthorities}</span>
                          </div>
                        )}
                        {(selectedEmployee as any).lastUpdate && (
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">Last Updated:</span>
                            <span>{new Date((selectedEmployee as any).lastUpdate).toLocaleDateString()}</span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default EmployeeGlobe;